import { classNames } from '@fullstory/util/fmt';
import React from 'react';
import { Flex } from '../../legacy/Flex';
import { Pane } from '../../legacy/Pane';
import { identifyComponent } from '../../util';
import { Text } from '../Text';
import css from './index.module.scss';

type Size = 'large' | 'medium' | 'small' | 'x-small';

interface RadioGroupProps<T extends SegmentedControlOptionType> {
  /** Selected option in the SegmentedControl */
  selected: T;
  /** Available options for the control */
  options: T[];
  /** Function called when selected option changes */
  onChange: (value: T) => void;
}

interface SegmentedControlProps<T extends SegmentedControlOptionType> extends RadioGroupProps<T> {
  size?: Size;
}

/**
 * A segmented control is a set of buttons typically used to switch views or settings on a page.
 */
export function SegmentedControl<T extends SegmentedControlOptionType>({
  options,
  selected,
  onChange,
  size = 'medium',
}: SegmentedControlProps<T>): React.ReactElement {
  const idx = options.findIndex(o => o.id == selected.id);

  return (
    <Pane
      data-component="SegmentedControl"
      width="100%"
      className={css.segmentControl}
      borderRadius={6}
      {...identifyComponent('segmented-control')}
    >
      <Flex className={css.textContainer} justifyContent="space-around" alignItems="stretch">
        {options.map(o => (
          <SegmentedControlOption<T>
            key={o.id}
            onChange={onChange}
            checked={selected && selected.id === o.id}
            option={o}
            size={size}
          />
        ))}
      </Flex>
      <div className={css.sliderContainer}>
        <div
          className={classNames(css.segmentSlider, {
            [css['x-small']]: size === 'x-small',
            [css.small]: size === 'small',
            [css.medium]: size === 'medium',
            [css.large]: size === 'large',
          })}
          style={{
            transform: `translate(${Math.max(idx, 0) * 100}%,0)`,
            width: `${100 / options.length}%`,
          }}
        />
      </div>
    </Pane>
  );
}

export interface SegmentedControlOptionType {
  id: string;
  label: string;
  description?: string;
}

interface RadioOptionProps<T extends SegmentedControlOptionType> {
  option: T;
  checked: boolean;
  size: Size;
  onChange: (value: T) => void;
}

function SegmentedControlOption<T extends SegmentedControlOptionType>(
  props: RadioOptionProps<T>,
): React.ReactElement {
  return (
    <label data-component="SegmentedControlOption" className={css.option}>
      <input
        type="radio"
        hidden
        checked={props.checked}
        onChange={() => {
          props.onChange(props.option);
        }}
      />
      {renderText(props.option.label, props.size, props.checked)}
    </label>
  );
}

const renderText = (label: string, size: Size, active: boolean) => {
  let variant: React.ComponentPropsWithRef<typeof Text>['appearance'] =
    size == 'x-small' ? 'bodyXSmall' : size == 'small' ? 'bodySmall' : 'bodyMedium';
  variant = active ? `${variant}Bold` : variant;

  return (
    <Text data-component="renderText" className={css.label} appearance={variant}>
      {label}
    </Text>
  );
};
